/**
 * One component renders every list surface on the platform.
 *
 * The server sends a declaration: columns with their kind and data class,
 * cells already formatted and already redacted, the filters with their options,
 * the page, the total, and the row scope that was applied. This renders it.
 *
 * It is generic for the same reason the server's renderer is generic. Eighty
 * hand-written tables would be eighty places to forget the row-scope note, the
 * audit button or the redaction, and a table that forgets redaction does not
 * look broken. Here there is one place, and a column a role may not see arrives
 * with visible false and a cell that was masked on the server before it was
 * serialised.
 *
 * Filtering and paging change the query string, which the resolver reads, so a
 * filtered table is a URL somebody can send to a colleague and the browser's
 * back button does what it should.
 */
import { Link, useSearchParams } from 'react-router-dom'

import type { Cell as CellData, ListPayload } from '../api/types'
import { useSession } from '../app/session'
import { appPath } from '../app/paths'
import { NUMERIC_KINDS } from '../format/format'
import { AuditButton } from './Audit'
import { Badge, Empty, Meter, Note } from './bits'

function Cell({ cell, label }: { cell: CellData; label: string }) {
  const numeric = NUMERIC_KINDS.has(cell.kind)
  const className = numeric ? 'num' : undefined

  let body: React.ReactNode = cell.text

  if (cell.kind === 'hidden') {
    // A class this role may not see. The dash is the server's: no value for the
    // column reached the browser, so there is nothing here to reveal.
    body = <span className="muted" title="Not visible to your role">—</span>
  } else if (cell.kind === 'badge' && cell.text) {
    body = <Badge tone={(cell.tone as never) || 'info'}>{cell.text}</Badge>
  } else if (cell.kind === 'bool') {
    body = <Badge tone={cell.value ? 'good' : 'muted'}>{cell.text}</Badge>
  } else if (cell.kind === 'code' || cell.kind === 'uuidshort' || cell.kind === 'ref') {
    body = <span className="mono clip">{cell.text}</span>
  } else if (cell.kind === 'json' || cell.kind === 'text') {
    body = <span className="clip" title={cell.text}>{cell.text}</span>
  } else if (cell.kind === 'pct' && cell.value !== null && cell.value !== undefined) {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 7,
                    justifyContent: 'flex-end' }}>
        <span>{cell.text}</span>
        <div style={{ width: 54 }}><Meter pct={Number(cell.value)} /></div>
      </div>
    )
  }

  return <td data-label={label} className={className}>{body}</td>
}

export function DataList({ d }: { d: ListPayload }) {
  const { lbl, t } = useSession()
  const [params, setParams] = useSearchParams()

  if (d.unavailable) {
    return (
      <Note kind="warn">
        <b>This section is registered but has no data declaration yet.</b> It
        exists in the catalogue with its permissions, its Joget element mapping
        and its explainer, which is what the permission matrix and the port
        inventory read. Its query declaration has not been written, so there is
        nothing to list. Saying so is more useful than rendering an empty table
        that looks like a result.
      </Note>
    )
  }

  if (d.denied_scope) {
    return (
      <Note kind="bad">
        <b>No rows are visible to you here.</b> Your role holds this section at
        row scope <b>{lbl(d.grant.row_scope)}</b>, and this query has no column
        to apply that scope on, or your account carries no value for it. The
        platform returns nothing rather than everything, which is the safe
        direction when a scope cannot be expressed.
      </Note>
    )
  }

  const setParam = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === '') merged.delete(key)
      else merged.set(key, value)
    }
    // Any change of filter or page size returns to the first page. Keeping the
    // page number would answer a narrowed filter with an empty table that looks
    // like no matches.
    if (!('page' in next)) merged.delete('page')
    setParams(merged, { replace: false })
  }

  const sort = params.get('sort') || ''
  const sortBy = (field: string) => {
    setParam({ sort: sort === field ? `-${field}` : field })
  }

  const visible = d.columns.filter((c) => c.visible)

  return (
    <div className="card">
      {d.filters.length > 0 && (
        <form className="filters" onSubmit={(e) => e.preventDefault()}>
          {d.filters.map((f) => (
            <div className={`field ${f.kind === 'ilike' ? 'wide' : ''}`} key={f.name}>
              <label htmlFor={`f_${f.name}`}>{f.label}</label>
              {f.options.length > 0 ? (
                <select id={`f_${f.name}`} value={params.get(`f_${f.name}`) || ''}
                        onChange={(e) => setParam({ [`f_${f.name}`]: e.target.value })}>
                  <option value="">Any</option>
                  {f.options.map((o) => (
                    <option value={o} key={o}>{lbl(o)}</option>
                  ))}
                </select>
              ) : (
                <input id={`f_${f.name}`} defaultValue={f.value} placeholder={f.label}
                       onKeyDown={(e) => {
                         if (e.key === 'Enter') {
                           setParam({ [`f_${f.name}`]: e.currentTarget.value })
                         }
                       }}
                       onBlur={(e) => {
                         if (e.currentTarget.value !== f.value) {
                           setParam({ [`f_${f.name}`]: e.currentTarget.value })
                         }
                       }} />
              )}
            </div>
          ))}
          <div className="field">
            <label htmlFor="size">Rows</label>
            <select id="size" value={String(d.size)}
                    onChange={(e) => setParam({ size: e.target.value })}>
              {[25, 50, 100, 200].map((s) => (
                <option value={s} key={s}>{s}</option>
              ))}
            </select>
          </div>
          {Object.keys(d.applied).length > 0 && (
            <button className="btn" type="button"
                    onClick={() => setParams(new URLSearchParams())}>Clear</button>
          )}
        </form>
      )}

      <div className="card-body tight">
        {d.rows.length === 0 ? (
          <Empty title="Nothing matches">
            {Object.keys(d.applied).length > 0
              ? 'Clear the filters to widen the search.'
              : 'There are no records here yet.'}
          </Empty>
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  {visible.map((c) => (
                    <th key={c.field}
                        className={NUMERIC_KINDS.has(c.kind) ? 'num' : undefined}
                        aria-sort={sort === c.field ? 'ascending'
                          : sort === `-${c.field}` ? 'descending' : 'none'}>
                      <button type="button" className="th-sort"
                              onClick={() => sortBy(c.field)}>{c.label}</button>
                    </th>
                  ))}
                  <th className="acts" />
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r, rowIndex) => (
                  <tr key={r.link || rowIndex}>
                    {d.columns.map((c, i) => (
                      c.visible
                        ? <Cell key={c.field} cell={r.cells[i]} label={c.label} />
                        : null
                    ))}
                    <td className="acts" data-label="Actions">
                      {r.link && (
                        <Link className="btn sm" to={appPath(r.link)}>Open</Link>
                      )}
                      {r.audit && <AuditButton link={r.audit} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-foot">
        <span>
          {d.total} record{d.total === 1 ? '' : 's'}
          {d.pages > 1 && ` · page ${d.page} of ${d.pages}`}
        </span>
        {d.immutable && (
          <span className="b muted">append only, no update or delete is permitted</span>
        )}
        {d.aggregate_only && <span className="b warn">aggregate scope</span>}
        <div className="grow" />
        {d.pages > 1 && (
          <div className="pager">
            {d.page > 1 && (
              <button type="button"
                      onClick={() => setParam({ page: String(d.page - 1) })}>
                {t('nav.previous', 'Previous')}
              </button>
            )}
            <span className="now">{d.page}</span>
            {d.page < d.pages && (
              <button type="button"
                      onClick={() => setParam({ page: String(d.page + 1) })}>
                {t('nav.next', 'Next')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
